import { useEffect, useState } from "react";
import { toast } from "sonner";
import { X, Plus, Trash2, Mail, ScanSearch, Send } from "lucide-react";
import api, { apiError } from "@/lib/api";

function TagInput({ values, onChange, placeholder, testId }) {
  const [input, setInput] = useState("");
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) onChange([...values, v]);
    setInput("");
  };
  return (
    <div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {values.map((v) => (
          <span key={v} className="inline-flex items-center gap-1 text-xs font-mono bg-secondary border border-border px-2 py-1">
            {v}
            <button data-testid={`${testId}-remove-${v}`} onClick={() => onChange(values.filter((x) => x !== v))} className="text-muted-foreground hover:text-destructive tap-btn"><X size={11} /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input data-testid={`${testId}-input`} className="tactical-input" placeholder={placeholder} value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }} />
        <button data-testid={`${testId}-add`} onClick={add} className="h-11 px-3 border border-border text-primary tap-btn"><Plus size={14} /></button>
      </div>
    </div>
  );
}

function Block({ title, children }) {
  return (
    <section className="soft-card mx-4 my-4 px-5 py-6">
      <p className="overline-label text-primary mb-4">{title}</p>
      <div className="space-y-4">{children}</div>
    </section>
  );
}

export default function Settings() {
  const [profile, setProfile] = useState(null);
  const [watchlist, setWatchlist] = useState([]);
  const [wUrl, setWUrl] = useState("");
  const [wLabel, setWLabel] = useState("");
  const [emailSettings, setEmailSettings] = useState(null);
  const [activity, setActivity] = useState([]);
  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState("");

  const load = () => {
    api.get("/profile").then((r) => setProfile(r.data));
    api.get("/watchlist").then((r) => setWatchlist(r.data));
    api.get("/email/settings").then((r) => setEmailSettings(r.data));
    api.get("/email/activity").then((r) => setActivity(r.data));
    api.get("/digest/preview").then((r) => setDigest(r.data));
  };
  useEffect(load, []);

  if (!profile || !emailSettings) return <p className="p-6 text-muted-foreground font-mono text-sm">LOADING...</p>;

  const saveProfile = async () => {
    try {
      const payload = { ...profile, min_value: Number(profile.min_value) || 0, max_value: Number(profile.max_value) || 0, expansion_threshold: Number(profile.expansion_threshold) || 0 };
      await api.put("/profile", payload);
      toast.success("Fit profile saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const addWatch = async () => {
    if (!wUrl.trim()) return;
    try {
      await api.post("/watchlist", { url: wUrl.trim(), label: wLabel.trim() });
      setWUrl(""); setWLabel("");
      api.get("/watchlist").then((r) => setWatchlist(r.data));
      toast.success("Added to watchlist — checked daily");
    } catch (e) { toast.error(apiError(e)); }
  };

  const saveEmail = async () => {
    try {
      await api.put("/email/settings", { sender_whitelist: emailSettings.sender_whitelist, keywords: emailSettings.keywords });
      toast.success("Email intake settings saved");
    } catch (e) { toast.error(apiError(e)); }
  };

  const runScan = async () => {
    setBusy("scan");
    try {
      const { data } = await api.post("/email/scan");
      toast.success(`Scanned ${data.scanned} emails — ${data.matched} new bid match${data.matched === 1 ? "" : "es"}`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const runExtract = async () => {
    setBusy("extract");
    try {
      const { data } = await api.post("/email/extract-tasks");
      toast.success(`AI scanned ${data.scanned} emails — ${data.suggested} suggested to-do${data.suggested === 1 ? "" : "s"} (see Today)`);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const sendDigest = async () => {
    setBusy("digest");
    try {
      const { data } = await api.post("/digest/send");
      data.delivered ? toast.success(data.detail) : toast.warning(data.detail);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(""); }
  };

  const set = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });

  return (
    <div data-testid="settings-page" className="pb-4">
      <div className="px-5 pt-8 pb-2">
        <p className="overline-label text-primary">Configuration</p>
        <h1 className="font-display text-4xl tracking-tight text-primary">Settings</h1>
      </div>

      <Block title="Fit Profile">
        <div>
          <p className="overline-label mb-1.5">NAICS Codes</p>
          <TagInput testId="naics" values={profile.naics_codes} onChange={(v) => setProfile({ ...profile, naics_codes: v })} placeholder="Add NAICS code..." />
        </div>
        <div>
          <p className="overline-label mb-1.5">Keywords</p>
          <TagInput testId="keyword" values={profile.keywords} onChange={(v) => setProfile({ ...profile, keywords: v })} placeholder="Add keyword..." />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <div><p className="overline-label mb-1">Min value $</p><input data-testid="min-value-input" type="number" className="tactical-input" value={profile.min_value} onChange={set("min_value")} /></div>
          <div><p className="overline-label mb-1">Max value $</p><input data-testid="max-value-input" type="number" className="tactical-input" value={profile.max_value} onChange={set("max_value")} /></div>
        </div>
        <div>
          <p className="overline-label mb-1.5">Geography</p>
          <p className="text-xs text-muted-foreground mb-2">Home: <span className="font-mono text-primary">{profile.home_states.join(", ")}</span> — all matches surface. Coverage states surface as READY. Everywhere else: EXPANSION only if above threshold or remote-friendly.</p>
          <TagInput testId="coverage-state" values={profile.coverage_states} onChange={(v) => setProfile({ ...profile, coverage_states: v.map((s) => s.toUpperCase()) })} placeholder="Add coverage state (e.g. PA)..." />
        </div>
        <div><p className="overline-label mb-1">Expansion value threshold $</p><input data-testid="expansion-threshold-input" type="number" className="tactical-input" value={profile.expansion_threshold} onChange={set("expansion_threshold")} /></div>
        <button data-testid="save-profile-button" onClick={saveProfile} className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm tap-btn">Save Fit Profile</button>
      </Block>

      <Block title="URL Watchlist">
        <p className="text-xs text-muted-foreground">Paste procurement page URLs (school districts, charter networks). Checked daily for new postings.</p>
        <input data-testid="watchlist-url-input" className="tactical-input" placeholder="https://district.org/procurement" value={wUrl} onChange={(e) => setWUrl(e.target.value)} />
        <div className="flex gap-2">
          <input data-testid="watchlist-label-input" className="tactical-input" placeholder="Label (optional)" value={wLabel} onChange={(e) => setWLabel(e.target.value)} />
          <button data-testid="watchlist-add-button" onClick={addWatch} className="h-11 px-4 bg-primary text-primary-foreground text-xs font-bold uppercase tracking-widest tap-btn">Add</button>
        </div>
        <div className="divide-y divide-border/60 bg-secondary/50 rounded-xl overflow-hidden">
          {watchlist.length === 0 && <p className="p-3 text-xs text-muted-foreground">No URLs watched yet.</p>}
          {watchlist.map((w) => (
            <div key={w.id} className="flex items-center gap-2 p-3">
              <span className={`w-2 h-2 rounded-full shrink-0 ${w.status === "ok" ? "bg-[#7A9B76]" : w.status === "error" ? "bg-[#C4674A]" : "bg-muted-foreground"}`} />
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">{w.label}</p>
                <p className="text-[10px] font-mono text-muted-foreground truncate">{w.url}</p>
              </div>
              <button data-testid={`watchlist-delete-${w.id}`} onClick={async () => { await api.delete(`/watchlist/${w.id}`); api.get("/watchlist").then((r) => setWatchlist(r.data)); }} className="text-destructive tap-btn p-2"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </Block>

      <Block title="Email Intake">
        <div data-testid="gmail-status" className="border border-amber-500/50 bg-amber-500/10 p-3 text-xs text-amber-400">
          <Mail size={13} className="inline mr-1.5" />Gmail not connected — running in DEMO MODE with sample emails. Read-only OAuth can be connected later. The system never sends, deletes, or modifies emails.
        </div>
        <div>
          <p className="overline-label mb-1.5">Sender whitelist</p>
          <TagInput testId="sender" values={emailSettings.sender_whitelist} onChange={(v) => setEmailSettings({ ...emailSettings, sender_whitelist: v })} placeholder="alerts@portal.com..." />
        </div>
        <div>
          <p className="overline-label mb-1.5">Trigger keywords</p>
          <TagInput testId="email-keyword" values={emailSettings.keywords} onChange={(v) => setEmailSettings({ ...emailSettings, keywords: v })} placeholder="RFP, solicitation..." />
        </div>
        <button data-testid="save-email-settings-button" onClick={saveEmail} className="w-full h-11 border border-primary text-primary font-bold uppercase tracking-widest text-xs tap-btn">Save Intake Settings</button>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="run-email-scan-button" onClick={runScan} disabled={busy === "scan"} className="h-12 bg-secondary border border-border text-foreground text-xs font-bold uppercase tracking-widest tap-btn disabled:opacity-50 flex items-center justify-center gap-1.5"><ScanSearch size={14} />{busy === "scan" ? "Scanning..." : "Scan for bids"}</button>
          <button data-testid="run-task-extract-button" onClick={runExtract} disabled={busy === "extract"} className="h-12 bg-secondary border border-border text-foreground text-xs font-bold uppercase tracking-widest tap-btn disabled:opacity-50 flex items-center justify-center gap-1.5"><ScanSearch size={14} />{busy === "extract" ? "AI parsing..." : "Extract to-dos (AI)"}</button>
        </div>
        <div>
          <p className="overline-label mb-1.5">Activity log</p>
          <div className="bg-secondary/50 rounded-xl divide-y divide-border/60 max-h-56 overflow-y-auto" data-testid="email-activity-log">
            {activity.length === 0 && <p className="p-3 text-xs text-muted-foreground">No scans yet.</p>}
            {activity.map((a) => (
              <div key={a.id} className="p-2.5 text-xs">
                <p className="font-mono text-muted-foreground">{new Date(a.ts).toLocaleString()} · {a.type === "bid_scan" ? "BID SCAN" : "TASK SCAN"}</p>
                <p>Scanned {a.scanned} → matched {a.matched}</p>
              </div>
            ))}
          </div>
        </div>
      </Block>

      <Block title="Daily Digest">
        <p className="text-xs text-muted-foreground">Sent every morning at 7:00 AM ET via Resend: top 3, follow-ups due, deadlines within 7 days, and overnight bid matches.</p>
        {digest?.last_sent && <p className="text-xs font-mono text-muted-foreground">Last: {digest.last_sent.sent_at?.slice(0, 16).replace("T", " ")} — {digest.last_sent.delivered ? "delivered" : digest.last_sent.detail}</p>}
        <button data-testid="send-digest-button" onClick={sendDigest} disabled={busy === "digest"} className="w-full h-12 bg-primary text-primary-foreground font-bold uppercase tracking-widest text-sm tap-btn disabled:opacity-50 flex items-center justify-center gap-2"><Send size={14} />{busy === "digest" ? "Sending..." : "Send digest now"}</button>
        {digest && (
          <div className="rounded-xl bg-white p-3 max-h-72 overflow-y-auto shadow-inner" data-testid="digest-preview">
            <div dangerouslySetInnerHTML={{ __html: digest.html }} />
          </div>
        )}
      </Block>
    </div>
  );
}
